'use client';

import { useCallback, useEffect, useRef, useState } from 'react';

import { EntrarLinkClient } from './entrar-link';
import { PUBLIC_NAV_ITEMS, type MegaMenuKey } from './nav';
import { PublicMegaMenu } from './public-mega-menu';

interface Props {
  entrarHref: string;
}

export function PublicHeader({ entrarHref }: Props) {
  const [scrolled, setScrolled] = useState(false);
  const [openMenu, setOpenMenu] = useState<MegaMenuKey | null>(null);
  const rootRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    function onScroll() {
      setScrolled(window.scrollY > 8);
    }
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  const close = useCallback(() => setOpenMenu(null), []);

  // ESC + clique fora fecham o mega-menu.
  useEffect(() => {
    if (!openMenu) return;
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') setOpenMenu(null);
    }
    function onMouseDown(e: MouseEvent) {
      const target = e.target as Node | null;
      if (target && rootRef.current && !rootRef.current.contains(target)) setOpenMenu(null);
    }
    window.addEventListener('keydown', onKey);
    document.addEventListener('mousedown', onMouseDown);
    return () => {
      window.removeEventListener('keydown', onKey);
      document.removeEventListener('mousedown', onMouseDown);
    };
  }, [openMenu]);

  return (
    <div
      ref={rootRef}
      className="pointer-events-none fixed inset-x-0 top-4 z-40 flex flex-col items-center px-4"
    >
      <nav
        className={[
          'pointer-events-auto relative flex items-center gap-4 overflow-hidden rounded-full',
          'border border-[rgba(222,219,246,0.16)] py-2 pr-2 pl-4 md:gap-5 md:pl-5',
          'bg-[linear-gradient(180deg,rgba(40,34,64,0.55),rgba(24,20,38,0.42))]',
          'backdrop-blur-xl backdrop-saturate-150',
          'shadow-[0_10px_40px_rgba(0,0,0,0.5),inset_0_1px_0_rgba(255,255,255,0.22),inset_0_-1px_0_rgba(0,0,0,0.25)]',
          'transition-shadow duration-300',
          scrolled
            ? 'shadow-[0_14px_50px_rgba(0,0,0,0.6),inset_0_1px_0_rgba(255,255,255,0.26)]'
            : '',
        ].join(' ')}
      >
        {/* brilho de vidro no topo */}
        <span
          aria-hidden
          className="pointer-events-none absolute inset-x-0 top-0 h-1/2 bg-[linear-gradient(180deg,rgba(255,255,255,0.10),transparent)]"
        />

        {/* logo → home */}
        <a
          href="/"
          className="relative z-[1] flex items-center gap-2 font-semibold tracking-tight text-white"
        >
          <img
            src="/icons/icon-192.png"
            width={26}
            height={26}
            alt=""
            className="block rounded-[7px]"
          />
          Trakd
        </a>

        {/* links centrais (só desktop) */}
        <div className="relative z-[1] hidden items-center gap-1 md:flex">
          {PUBLIC_NAV_ITEMS.map((item) =>
            item.href ? (
              <a
                key={item.label}
                href={item.href}
                className="text-ink-muted hover:text-ink rounded-full px-3.5 py-2 text-sm font-medium transition-colors hover:bg-[rgba(222,219,246,0.10)]"
              >
                {item.label}
              </a>
            ) : (
              <button
                key={item.label}
                type="button"
                aria-expanded={openMenu === item.menu}
                onClick={() =>
                  setOpenMenu((cur) => (cur === item.menu ? null : (item.menu ?? null)))
                }
                className={[
                  'text-ink-muted hover:text-ink rounded-full px-3.5 py-2 text-sm font-medium transition-colors hover:bg-[rgba(222,219,246,0.10)]',
                  openMenu === item.menu ? 'text-ink bg-[rgba(222,219,246,0.10)]' : '',
                ].join(' ')}
              >
                {item.label}
              </button>
            ),
          )}
        </div>

        {/* botão Acessar (gradiente · dispara InitiateCheckout) */}
        <EntrarLinkClient
          href={entrarHref}
          contentName="acessar-header"
          className="bg-trakd-fill text-on-brand shadow-trakd-glow-violet relative z-[1] rounded-full px-5 py-2.5 text-sm font-semibold transition hover:brightness-110"
        >
          Acessar
        </EntrarLinkClient>
      </nav>

      {/* mega-menu (popover bento) abaixo do dock · só desktop */}
      {openMenu ? (
        <div className="pointer-events-none mt-2 hidden w-full justify-center md:flex">
          <PublicMegaMenu menu={openMenu} onNavigate={close} />
        </div>
      ) : null}
    </div>
  );
}
